import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { apiCall } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateAll as invalidateAllTickets, useCreateTicket, uploadAdjuntoTicket } from "./api";
import {
  TICKET_CATEGORIA_LABEL,
  TICKET_PRIORIDAD_LABEL,
  ticketCategoriaEnum,
  ticketPrioridadEnum,
  type TicketCategoria,
  type TicketCreate,
  type TicketPrioridad,
} from "./schemas";

type ClienteSearchResult = {
  id: string;
  codigo: string;
  razon_social: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  /** Si se pasa, salta el cliente picker y crea sobre ese cliente. */
  fixedClienteId?: string;
  fixedClienteLabel?: string;
};

/**
 * Dialog para abrir un ticket nuevo desde el panel admin.
 *
 * Cliente picker es básico: input que busca por código (CL-XXXX) o
 * razón social. Pega al endpoint `/api/v1/clientes` con `search=`
 * y muestra hasta 5 resultados. Al seleccionar uno, sigue con el form.
 *
 * Si el ticket nace desde el detail de un cliente específico, se puede
 * pasar `fixedClienteId` y se omite el picker.
 */
export function NewTicketDialog({ open, onClose, fixedClienteId, fixedClienteLabel }: Props) {
  const navigate = useNavigate();
  const create = useCreateTicket();
  const qc = useQueryClient();

  const [clienteId, setClienteId] = useState<string | null>(fixedClienteId ?? null);
  const [clienteLabel, setClienteLabel] = useState<string>(fixedClienteLabel ?? "");
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<ClienteSearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  const [categoria, setCategoria] = useState<TicketCategoria>("soporte_tecnico");
  const [prioridad, setPrioridad] = useState<TicketPrioridad>("normal");
  const [asunto, setAsunto] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  if (!open) return null;

  async function doSearch() {
    if (!search.trim()) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const data = await apiCall<{ items: ClienteSearchResult[] }>(
        "get",
        "/api/v1/clientes",
        {
          searchParams: { search: search.trim(), size: 5, page: 1 },
        },
      );
      setResults(data.items ?? []);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }

  function selectCliente(c: ClienteSearchResult) {
    setClienteId(c.id);
    setClienteLabel(`${c.razon_social} (${c.codigo})`);
    setResults([]);
    setSearch("");
  }

  function reset() {
    setClienteId(fixedClienteId ?? null);
    setClienteLabel(fixedClienteLabel ?? "");
    setSearch("");
    setResults([]);
    setCategoria("soporte_tecnico");
    setPrioridad("normal");
    setAsunto("");
    setDescripcion("");
    setAttachment(null);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!clienteId) {
      toast.error("Selecciona un cliente primero");
      return;
    }
    if (asunto.trim().length < 3 || descripcion.trim().length < 3) {
      toast.error("Asunto y descripción son obligatorios");
      return;
    }

    const payload: TicketCreate = {
      cliente_id: clienteId,
      categoria,
      prioridad,
      asunto: asunto.trim(),
      descripcion: descripcion.trim(),
    };

    try {
      const ticket = await create.mutateAsync(payload);

      // Subir adjunto si hay (sin comentario_id → queda como inicial del ticket)
      if (attachment) {
        setIsUploading(true);
        try {
          await uploadAdjuntoTicket(ticket.id, attachment);
          // Forzar refetch del detail recién creado pa' que la próxima
          // vista del ticket incluya el adjunto sin necesidad de F5
          invalidateAllTickets(qc);
        } catch (err) {
          toast.error(
            `Ticket creado, falló adjuntar: ${(err as Error).message ?? "Error"}`,
          );
        } finally {
          setIsUploading(false);
        }
      }

      toast.success(`Ticket ${ticket.codigo} creado`);
      reset();
      onClose();
      navigate(`/tickets/${ticket.id}`);
    } catch (err) {
      const msg = (err as { message?: string }).message ?? "Error al crear ticket";
      toast.error(msg);
    }
  }

  return (
    <div
      // rc137 — móvil: hoja a pantalla completa (mismo patrón que ui/dialog.tsx)
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm max-md:items-stretch"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-zinc-950 rounded-xl border border-zinc-200 dark:border-zinc-800 w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-2xl max-md:flex max-md:max-h-none max-md:max-w-none max-md:flex-col max-md:rounded-none max-md:border-0 max-md:sheet-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100 dark:border-zinc-900 max-md:pt-[calc(1rem+env(safe-area-inset-top))]">
          <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
            Abrir nuevo ticket
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4 max-md:flex max-md:flex-1 max-md:flex-col max-md:p-4">
          {/* Cliente picker */}
          {!fixedClienteId && (
            <div>
              <label className="text-[10px] uppercase tracking-wider font-medium text-zinc-500 mb-1.5 block">
                Cliente
              </label>
              {clienteId ? (
                <div className="flex items-center justify-between px-3 py-2 bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md">
                  <span className="text-sm text-zinc-900 dark:text-zinc-100">{clienteLabel}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setClienteId(null);
                      setClienteLabel("");
                    }}
                    className="text-xs text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
                  >
                    Cambiar
                  </button>
                </div>
              ) : (
                <div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          doSearch();
                        }
                      }}
                      placeholder="Buscar por código (CL-XXXX) o razón social…"
                      className="flex-1 text-sm px-3 py-2 rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-400"
                    />
                    <button
                      type="button"
                      onClick={doSearch}
                      disabled={searching}
                      className="px-3 py-2 text-sm font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 rounded-md hover:bg-zinc-200 dark:hover:bg-zinc-700 disabled:opacity-60"
                    >
                      {searching ? <Loader2 size={14} className="animate-spin" /> : "Buscar"}
                    </button>
                  </div>
                  {results.length > 0 && (
                    <div className="mt-2 border border-zinc-200 dark:border-zinc-800 rounded-md max-h-48 overflow-y-auto">
                      {results.map((c) => (
                        <button
                          type="button"
                          key={c.id}
                          onClick={() => selectCliente(c)}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-zinc-50 dark:hover:bg-zinc-900 border-b last:border-b-0 border-zinc-100 dark:border-zinc-900"
                        >
                          <div className="font-medium">{c.razon_social}</div>
                          <div className="text-xs font-mono text-zinc-500">{c.codigo}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Categoría + Prioridad */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-medium text-zinc-500 mb-1.5 block">
                Categoría
              </label>
              <select
                value={categoria}
                onChange={(e) => setCategoria(e.target.value as TicketCategoria)}
                className="w-full text-sm px-3 py-2 rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900"
              >
                {ticketCategoriaEnum.map((c) => (
                  <option key={c} value={c}>
                    {TICKET_CATEGORIA_LABEL[c]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-medium text-zinc-500 mb-1.5 block">
                Prioridad
              </label>
              <select
                value={prioridad}
                onChange={(e) => setPrioridad(e.target.value as TicketPrioridad)}
                className="w-full text-sm px-3 py-2 rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900"
              >
                {ticketPrioridadEnum.map((p) => (
                  <option key={p} value={p}>
                    {TICKET_PRIORIDAD_LABEL[p]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Asunto */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-medium text-zinc-500 mb-1.5 block">
              Asunto
            </label>
            <input
              type="text"
              value={asunto}
              onChange={(e) => setAsunto(e.target.value)}
              maxLength={200}
              placeholder="Resumen breve del problema o solicitud"
              className="w-full text-sm px-3 py-2 rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900"
            />
          </div>

          {/* Descripción */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-medium text-zinc-500 mb-1.5 block">
              Descripción
            </label>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              rows={5}
              maxLength={10_000}
              placeholder="Detalle del problema, qué reportó el cliente, contexto…"
              className="w-full text-sm px-3 py-2 rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 resize-y"
            />
          </div>

          {/* Adjunto opcional (v1.4.0) */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-medium text-zinc-500 mb-1.5 block">
              Foto / PDF (opcional)
            </label>
            {attachment ? (
              <div className="flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1.5 dark:border-zinc-700 dark:bg-zinc-900">
                {attachment.type.startsWith("image/") ? (
                  <img
                    src={URL.createObjectURL(attachment)}
                    alt={attachment.name}
                    className="size-12 rounded object-cover"
                  />
                ) : (
                  <div className="flex size-12 items-center justify-center rounded bg-zinc-200 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
                    <Paperclip size={16} />
                  </div>
                )}
                <div className="flex-1 overflow-hidden text-xs">
                  <div className="truncate text-zinc-700 dark:text-zinc-200">
                    {attachment.name}
                  </div>
                  <div className="text-[10px] text-zinc-500">
                    {(attachment.size / 1024).toFixed(0)} KB
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setAttachment(null)}
                  className="rounded p-1 text-zinc-400 hover:bg-zinc-200 hover:text-zinc-700 dark:hover:bg-zinc-700 dark:hover:text-zinc-200"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-zinc-300 bg-zinc-50 px-3 py-2 text-xs text-zinc-500 hover:border-zinc-400 hover:text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400 dark:hover:border-zinc-500 dark:hover:text-zinc-200">
                <Paperclip size={14} />
                Adjuntar foto o PDF (máx 10 MB)
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    if (f && f.size > 10 * 1024 * 1024) {
                      toast.error("Máximo 10 MB");
                      return;
                    }
                    setAttachment(f);
                    e.target.value = "";
                  }}
                />
              </label>
            )}
          </div>

          {/* Acciones — rc137 móvil: pie pegado abajo con área segura */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100 dark:border-zinc-900 max-md:sticky max-md:bottom-0 max-md:!mt-auto max-md:-mx-4 max-md:bg-white max-md:px-4 max-md:pb-[calc(0.75rem+env(safe-area-inset-bottom))] max-md:pt-3 dark:max-md:bg-zinc-950 max-md:[&>button]:flex-1 max-md:[&>button]:justify-center">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={create.isPending || isUploading || !clienteId}
              className={cn(
                "px-4 py-1.5 text-sm font-medium rounded-md flex items-center gap-1.5",
                "bg-black hover:bg-zinc-800 text-white",
                "disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            >
              {(create.isPending || isUploading) && (
                <Loader2 size={14} className="animate-spin" />
              )}
              {isUploading ? "Subiendo foto…" : "Crear ticket"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
